import { createContext, useContext, ReactNode, useEffect } from 'react';
import { Box, Icon, Text, Button, Title, Link } from '@nimbus-ds/components';
import { StatsIcon, CloseIcon } from '@nimbus-ds/icons';
import { useResponsive } from 'commons/components/Responsive';
import { useControlCta } from 'commons/hooks/useControlCta';
import goToAdmin from 'commons/utils/gotToAdmin';
import {
  trackingNuvemPagoNudgesCalloutActive,
  trackingNuvemPagoNudgesCalloutView,
  trackingNuvemPagoNudgesCalloutClose,
} from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';

interface CalloutContextType {
  isMobile: boolean;
  handleActionPrimary: () => void;
  handleDismissCallout: () => void;
}

interface CalloutProps {
  children: ReactNode;
}

interface CalloutIconProps {
  children?: ReactNode;
}

interface CalloutContentProps {
  title: string;
  body: string;
  actionText: string;
}

interface CalloutActionsProps {
  actionText: string;
}

const CalloutContext = createContext<CalloutContextType | null>(null);

const useCallout = () => {
  const context = useContext(CalloutContext);
  if (!context) {
    throw new Error(
      'Callout compound components must be used within CalloutCard',
    );
  }
  return context;
};

const useNuvemPagoActions = () => {
  const { isCalloutVisible, handleDismissCallout, group } = useControlCta();
  const goToPayments = goToAdmin('/nuvempago/settings');
  const { isMobile } = useResponsive();

  const handleActionPrimary = () => {
    goToPayments();
    trackingNuvemPagoNudgesCalloutActive(group);
  };

  const handleControlDismissCallout = () => {
    trackingNuvemPagoNudgesCalloutClose(group);
    handleDismissCallout();
  };

  return {
    isCalloutVisible,
    isMobile,
    handleActionPrimary,
    handleDismissCallout: handleControlDismissCallout,
  };
};

function CalloutIcon({ children }: CalloutIconProps) {
  return (
    <Box
      padding="3"
      backgroundColor="primary-surfaceHighlight"
      borderRadius="2"
      borderColor="primary-surface"
      data-testid="callout-icon"
    >
      {children || (
        <Icon source={<StatsIcon size="medium" />} color="primary-textLow" />
      )}
    </Box>
  );
}

function CalloutContent({ title, body, actionText }: CalloutContentProps) {
  const { isMobile, handleActionPrimary } = useCallout();

  return (
    <Box maxWidth="80%">
      <Title as="h5" color="primary-textLow" data-testid="callout-title">
        {title}
      </Title>
      <Text color="primary-textLow" data-testid="callout-body">
        {body}
      </Text>
      <Box paddingTop="2" display={isMobile ? 'flex' : 'none'}>
        <Link
          appearance="neutral"
          onClick={handleActionPrimary}
          className="pt-2"
          data-testid="callout-action-mobile"
        >
          {actionText}
        </Link>
      </Box>
    </Box>
  );
}

function CalloutActions({ actionText }: CalloutActionsProps) {
  const { isMobile, handleActionPrimary, handleDismissCallout } = useCallout();

  return (
    <>
      <Box display={isMobile ? 'none' : 'flex'} alignItems="center" gap="4">
        <Button
          appearance="neutral"
          onClick={handleActionPrimary}
          data-testid="callout-action-desktop"
        >
          {actionText}
        </Button>
      </Box>
      <Box
        display={isMobile ? 'none' : 'flex'}
        cursor="pointer"
        paddingRight="2"
        onClick={handleDismissCallout}
        data-testid="callout-dismiss"
      >
        <Icon source={<CloseIcon size="small" />} color="primary-textLow" />
      </Box>
    </>
  );
}

function CalloutMobileDismiss() {
  const { isMobile, handleDismissCallout } = useCallout();

  if (!isMobile) return null;

  return (
    <Box
      cursor="pointer"
      position="absolute"
      top="10px"
      right="10px"
      display="flex"
      justifyContent="flex-start"
      alignItems="flex-start"
      onClick={handleDismissCallout}
      data-testid="callout-dismiss-mobile"
    >
      <Icon source={<CloseIcon size="small" />} color="primary-textLow" />
    </Box>
  );
}

function CalloutCard({ children }: CalloutProps) {
  const {
    isCalloutVisible,
    isMobile,
    handleActionPrimary,
    handleDismissCallout,
  } = useNuvemPagoActions();

  if (!isCalloutVisible) return null;

  return (
    <CalloutContext.Provider
      value={{ isMobile, handleActionPrimary, handleDismissCallout }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding="2"
        backgroundColor="primary-surface"
        borderRadius="3"
        borderColor="primary-interactive"
        borderWidth="1"
        borderStyle="solid"
        position="relative"
        data-testid="callout-card"
      >
        {children}
      </Box>
    </CalloutContext.Provider>
  );
}

export function CalloutCardActivateNuvemPago() {
  const { group } = useControlCta();
  const t = useTranslationOrders();
  const title = t('calloutCardActivateNuvemPago.title');
  const body = t('calloutCardActivateNuvemPago.body');
  const actionText = t('calloutCardActivateNuvemPago.actionPrimary');

  useEffect(() => {
    trackingNuvemPagoNudgesCalloutView(group);
  }, [group]);
  return (
    <CalloutCard>
      <Box display="flex" alignItems="center" gap="4">
        <CalloutIcon />
        <CalloutContent title={title} body={body} actionText={actionText} />
        <CalloutMobileDismiss />
      </Box>
      <Box display="flex" alignItems="center" gap="4">
        <CalloutActions actionText={actionText} />
      </Box>
    </CalloutCard>
  );
}

CalloutCard.Icon = CalloutIcon;
CalloutCard.Content = CalloutContent;
CalloutCard.Actions = CalloutActions;
CalloutCard.MobileDismiss = CalloutMobileDismiss;

export { CalloutCard };
